import { fromWei, toDecimal } from "web3-utils";
import { FormState } from "final-form";
import { Web3Provider } from "@ethersproject/providers";
import { toBigInt } from "ethers";
import { MultiFormValue } from "../pages/Scan/components/MultiTextInput";
import ApiRoutes from "../config/api.routes";

const debug = false;

/**
 * @desc Override of console.log() method
 *
 * @param args - items to log into browser console
 */
export const consoleLog = (...args: any[]): void => {
  if (debug) {
    console.log("%c Service: ", "color:orange;", ...args);
  }
};
/**
 * @desc Responsive spacing pair for MUI `sx` — the returned object IS the
 * breakpoint map, so pass it directly (`sx={{ padding: sizing(6) }}`).
 *
 * Never nest it inside another breakpoint map: `{ xs: "18px", md: sizing(6) }`
 * hands MUI an object where it expects a scalar, which resolves to `NaNpx` and
 * is dropped by the browser — the padding silently disappears. Reach for the
 * member you want instead: `{ xs: "18px", md: sizing(6).md }`.
 */
export const sizing = (value: number) => ({
  xs: value,
  md: value * 2,
});
/**
 * @desc Override of console.error() method
 *
 * @param args - items to log into browser console
 */
export const consoleError = (...args: any[]) => {
  console.log("%c Error: ", "color:red;", ...args);
};

export const isFormModified = (modified: FormState<any>["modified"]) => {
  let flag = false;

  if (modified) {
    Object.keys(modified).forEach((key) => {
      if (modified && modified?.[key] && modified[key]) {
        flag = true;
      }
    });
  }
  return flag;
};

export const numberWithCommas = (x: string | number) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const getScoreColor = (score: number) => {
  const _score = score > 1 ? score / 100 : score;
  const hue = (_score * 120).toString(10);
  return ["hsl(", hue, ",100%,50%)"].join("");
};

export const getRSVFromSignature = (sign: string) => {
  const signature = sign.substring(2);
  const r = "0x" + signature.slice(0, 64);
  const s = "0x" + signature.slice(64, 128);
  const v = "0x" + signature.slice(128, 130);
  const v_decimal = toDecimal(v);
  return {
    r,
    s,
    v,
    v_decimal,
  };
};

export const isAddressesEqual = (address1?: string, address2?: string) => {
  if (!address1 || !address2) {
    return false;
  }
  return address1.toUpperCase() === address2.toUpperCase();
};

export const sleep = async (ms: number) => {
  return await new Promise((resolve) => {
    setTimeout(() => {
      resolve("ok");
    }, ms);
  });
};

export const scaleBetween = (number: number, range: [number, number], range2: [number, number]) => {
  const x1 = range[0],
    x2 = range[1],
    x3 = range2[0],
    x4 = range2[1];

  return ((number - x1) / (x2 - x1)) * (x4 - x3) + x3;
};

export const toReadable = (
  value: string | number | bigint,
  wei = false,
  format = true,
  decimals = 0,
  currency = ""
) => {
  let _value = value;
  if (typeof value === "string") {
    _value = toBigInt(value);
  }
  let _wei = wei || typeof _value === "bigint";
  if (typeof value === "string" && value.length < 10) {
    _wei = false;
  }
  if (_wei) {
    const number = Number(fromWei(_value?.toString(), "ether"));
    if (format) {
      return (
        number.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: decimals,
        }) + (currency ? ` ${currency}` : "")
      );
    } else {
      return number;
    }
  } else {
    if (format) {
      return (
        Number(value).toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: decimals,
        }) + (currency ? ` ${currency}` : "")
      );
    } else {
      return Number(value);
    }
  }
};

export const checkTransactionConfirmation = async (hash: string, library?: Web3Provider) => {
  try {
    // Define explicit return type for the recursive function
    const checkTransactionLoop = async (): Promise<boolean> => {
      let receipt;

      // Always use window.ethereum directly when available
      if (window.ethereum) {
        receipt = await window.ethereum.request({
          method: "eth_getTransactionReceipt",
          params: [hash],
        });
      } else if (library) {
        // Fallback to library if window.ethereum is not available (should be rare)
        console.warn("Using legacy Web3Provider. Consider updating to use window.ethereum directly.");
        receipt = await library.send("eth_getTransactionReceipt", [hash]);
      } else {
        throw new Error("No Ethereum provider available");
      }

      if (receipt != null) {
        console.log({ receipt });
        return true;
      } else {
        // Wait a bit before checking again to avoid hammering the provider
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return checkTransactionLoop();
      }
    };

    return await checkTransactionLoop();
  } catch (e) {
    console.log({ error: e });
    return false;
  }
};
export const checkTransactionConfirmationReceipt = async (hash: string, library?: Web3Provider) => {
  try {
    // Add explicit return type to fix TypeScript error
    const checkTransactionLoop = async (): Promise<any> => {
      let receipt;

      if (library) {
        // Use the provided library if available
        receipt = await library.send("eth_getTransactionReceipt", [hash]);
      } else if (window.ethereum) {
        // Otherwise use window.ethereum directly
        receipt = await window.ethereum.request({
          method: "eth_getTransactionReceipt",
          params: [hash],
        });
      } else {
        throw new Error("No Ethereum provider available");
      }

      if (receipt != null) {
        console.log({ receipt });
        return receipt;
      } else {
        // Wait a bit before checking again to avoid hammering the provider
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return checkTransactionLoop();
      }
    };

    return await checkTransactionLoop();
  } catch (e) {
    console.log({ error: e });
    return e;
  }
};

export const getCountriesList = async (axios: any, setCountriesList: any) => {
  try {
    const storage = localStorage.getItem("countries");
    if (!storage) {
      const res = await axios.get(ApiRoutes.user.getAvailableCountries).then((res: any) => res.data);
      // const res = await axios.get("https://restcountries.com/v3.1/all?fields=name").then((res: any) => res.data);
      const counties = res
        .map((item: any) => ({
          label: item.name.common,
          value: item.name.common,
          icon: item.flags?.svg,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));
      localStorage.removeItem("countries");
      localStorage.setItem("countries", JSON.stringify(counties));
      setCountriesList(counties);
    } else {
      setCountriesList(JSON.parse(storage));
    }
  } catch (e) {
    console.log({ e });
  }
};

export const getAvailableCountriesList = async (axios: any, setCountriesList: any) => {
  try {
    const res = await axios.get(ApiRoutes.user.getAvailableCountries).then((res: any) => res.data);
    // const res = await axios.get("https://restcountries.com/v3.1/all?fields=name").then((res: any) => res.data);
    const counties = res
      .map((item: any) => ({
        label: item.name.common,
        value: item.name.common,
        icon: item.flags?.svg,
      }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
    localStorage.removeItem("countries");
    localStorage.setItem("countries", JSON.stringify(counties));
    setCountriesList(counties);
  } catch (e) {
    console.log({ e });
  }
};

// Full country list, for fields that record where a person actually lives (e.g. a patron's
// own country). getAvailableCountriesList above only returns countries that have an
// ecoFutures admin — that restriction exists for trust/guardian onboarding and would leave a
// patron unable to pick their country. Not cached in localStorage: the endpoint is public and
// server-cached for 24h, and the "countries" key holds the restricted list.
// The synthetic "Global" entry the endpoint prepends is dropped — it's an admin-scope marker,
// not a place someone lives.
export const getAllCountriesList = async (axios: any, setCountriesList: any) => {
  try {
    const res = await axios.get(ApiRoutes.user.getAllCountries).then((res: any) => res.data);
    const countries = (res || [])
      .filter((item: any) => item?.name?.common && item.name.common !== "Global")
      .map((item: any) => ({
        label: item.name.common,
        value: item.name.common,
        icon: item.flags?.svg,
      }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));
    setCountriesList(countries);
  } catch (e) {
    consoleError("Failed to fetch countries", e);
  }
};

export function formatLink(url?: string, protocol = false): string {
  if (!url) {
    return "-";
  }
  try {
    // Attempt to parse the URL
    const parsedUrl = new URL(url);

    // If it already has http or https, return just the origin
    if (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") {
      if (protocol) {
        return parsedUrl.origin;
      }
      return parsedUrl.host;
    }
  } catch (e) {
    // If the URL constructor throws an error, the URL is likely missing a protocol
  }

  // If there's no protocol, prepend "http://" and return just the origin
  if (protocol) {
    return new URL("https://" + url).origin;
  } else {
    return new URL("https://" + url).host;
  }
}

export const setMultiFormValues = (array: string[]): MultiFormValue[] =>
  array
    ? array.map((item, index) => ({
        key: index,
        label: item,
      }))
    : [];

export const getMultiFormValues = (array: MultiFormValue[]): string[] => (array ? array.map((item) => item.label) : []);

export const sixDigits = (value: number | string): number | string => {
  // Remove commas from string values before parsing
  const cleanValue = typeof value === "string" ? value.replace(/,/g, "") : value;
  const numValue = typeof cleanValue === "string" ? parseFloat(cleanValue) : cleanValue;

  // Check if the value is a valid number
  if (isNaN(numValue)) {
    return value;
  }

  // Convert to string to check for decimals and count digits (without commas)
  const stringValue = numValue.toString();

  // Check if the number has decimals
  if (!stringValue.includes(".")) {
    // No decimals, return the same value
    return value;
  }

  // Split by decimal point
  const [integerPart, decimalPart] = stringValue.split(".");
  const totalDigits = integerPart.length + decimalPart.length;

  // If total digits <= 6, return the same value
  if (totalDigits <= 6) {
    return value;
  }

  // If more than 6 digits with decimals, truncate to 6 total digits
  const maxDecimalPlaces = Math.max(0, 6 - integerPart.length);
  const result = parseFloat(numValue.toFixed(maxDecimalPlaces));

  // If original input was a comma-formatted string, return as formatted string
  if (typeof value === "string" && value.includes(",")) {
    return result.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDecimalPlaces,
    });
  }

  return result;
};

// Whether the secondary market is still locked for this NFT (V9.1). EcoFuturesToken's one-way
// _primaryMarketCompleted flag gates canTradeSecondary(), so every listing and offer reverts
// until it is set — the flag is mirrored onto etNft by the PrimaryMarketCompleted event.
//
// The settlementState check is not redundant: the column is `notNullable default false` with no
// backfill, so every pre-V9 NFT reads false forever (that event can only fire on the new
// contracts). Blocking on the flag alone would wrongly lock legacy tokens out of listing, so we
// only block when the token is demonstrably in a V9 settlement.
export const isSecondaryMarketLocked = (etNft?: {
  primaryMarketCompleted?: boolean | null;
  settlementState?: string | null;
} | null): boolean =>
  etNft?.primaryMarketCompleted === false && !!etNft?.settlementState && etNft.settlementState !== "none";

// An auction's standing price, in wei: its highest bid, or the opening price until there is one.
// AuctionCreation lists every auction with endingPrice 0 and the contract only writes a bid into
// it, so endingPrice on its own reads $0 for an auction nobody has bid on yet.
export const auctionCurrentPrice = (
  auction?: { startingPrice?: string | null; endingPrice?: string | null } | null
): string | undefined => (Number(auction?.endingPrice) > 0 ? auction?.endingPrice : auction?.startingPrice) ?? undefined;
